// Maps an axios error from the MFA endpoints to what the UI shows.
// Contract: docs/specs/authenticator-mfa-api.md (error table). The server's own `message`
// is never shown — only the strings below — so nothing about a code leaks through the UI.
const GENERIC = "Something went wrong. Please try again.";
const UNREACHABLE = "We couldn't reach the server. Try again.";

const attemptsText = (n) =>
  Number.isInteger(n) && n >= 0 ? ` ${n} ${n === 1 ? "attempt" : "attempts"} left.` : "";

const lockedText = (seconds) => {
  const minutes = Math.max(1, Math.ceil((Number(seconds) || 60) / 60));
  return `Too many attempts. Try again in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
};

const BY_CODE = {
  INVALID_CREDENTIALS: () => "Invalid email and/or password.",
  INVALID_CODE: (e) => `That code didn't work. Check it and try again.${attemptsText(e.attempts_left)}`,
  MFA_TX_EXPIRED: () => "Your sign-in timed out.",
  MFA_LOCKED: (e) => lockedText(e.retry_after),
  METHOD_UNAVAILABLE: () => "That method isn't available for your account.",
  STEP_UP_REQUIRED: () => "Please confirm it's you again.",
  ENROLLMENT_EXPIRED: () => "Setup timed out. Start again.",
  INVALID_REQUEST: () => "Please check what you entered.",
  ALREADY_ACTIVE: () => "Your authenticator status changed. Refreshing.",
  NOT_ACTIVE: () => "Your authenticator status changed. Refreshing.",
  SESSION_REQUIRED: () => "Please sign in again.",
  UPSTREAM_UNAVAILABLE: () => UNREACHABLE,
  // Phase 7b: Oracle's password rule (docs/specs/password-reset-stepup.md)
  WEAK_PASSWORD: () => "Use 10 to 128 characters with at least one letter and one number, and not your email.",
  // Login setup (docs/specs/login-setup.md §2)
  SETUP_REQUIRED: () => "Please sign in again to finish setting up your account.",
  INVALID_PHONE: () => "Enter a 10-digit US mobile number.",
};
// Codes after which this sign-in's tx no longer matches Oracle's: start over.
const RESTART = ["MFA_TX_EXPIRED", "SETUP_REQUIRED"];
// login/start with no method at all (docs/specs/login-setup.md §1): not "That method isn't available".
const NO_METHOD = () => "We can't send you a sign-in code. Please call support at 888-587-9757.";

// A wrong SSN/TIN is not a "code" to the user (wording: docs/specs/mfa-ux-cleanup.md §3).
const TIN_INVALID = (e) =>
  `That doesn't match the SSN or TIN on your loan. Check the 4 digits and try again.${attemptsText(e.attempts_left)}`;
// Oracle's tin_offer_after_sec: the TIN is not accepted yet (docs/specs/authenticator-mfa-doc-gaps.md, part 1).
const TIN_NOT_YET = (e) => {
  const minutes = Math.max(1, Math.ceil(e.retry_after / 60));
  return `You can verify with your SSN/TIN in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
};
const tinText = (body) => {
  if (body.code === "INVALID_CODE") return TIN_INVALID;
  if (body.code === "METHOD_UNAVAILABLE" && body.retry_after > 0) return TIN_NOT_YET;
  return null;
};

const startText = (body) => (body.code === "METHOD_UNAVAILABLE" ? NO_METHOD : null);

export const describeMfaError = (error, { method, stage } = {}) => {
  const body = error?.response?.data?.error;
  if (body && BY_CODE[body.code]) {
    const describe = (method === "tin" && tinText(body)) || (stage === "start" && startText(body)) || BY_CODE[body.code];
    return {
      code: body.code,
      message: describe(body),
      restart: RESTART.includes(body.code),
      reopenStepUp: body.code === "STEP_UP_REQUIRED",
      refresh: ["ALREADY_ACTIVE", "NOT_ACTIVE"].includes(body.code),
      signIn: body.code === "SESSION_REQUIRED",
    };
  }
  if (error && !error.response) {
    return {
      code: "NETWORK", message: UNREACHABLE, restart: false, reopenStepUp: false, refresh: false, signIn: false,
    };
  }
  return {
    code: body?.code ?? "UNKNOWN", message: GENERIC, restart: false, reopenStepUp: false, refresh: false, signIn: false,
  };
};
